<template>
  <OmtLayoutContainer class="hub-program-detail">
    <OmtRow :display="['none', 'flex']">
      <HubBreadcrumb :items="breadcrumb" />
    </OmtRow>

    <OmtRow v-if="program.image">
      <img
        :src="program.image"
        :alt="program.title"
        class="hub-program-detail__cover"
      />
    </OmtRow>

    <OmtRow
      mt="16px"
      stack
    >
      <div class="hub-program-detail__intro">
        <OmtTypography
          as="h1-bold"
          color="var(--colors-light-primary-navy-blue)"
        >
          {{ program.title }}
        </OmtTypography>
        <OmtTypography
          as="p1"
          color="var(--colors-light-primary-gray1)"
          style="white-space: pre-line"
        >
          {{ program.description }}
        </OmtTypography>
        <OmtTypography
          v-if="program.intro"
          as="p1"
          color="var(--colors-light-primary-gray1)"
        >
          {{ program.intro }}
        </OmtTypography>
      </div>
    </OmtRow>

    <OmtRow
      v-if="program.highlights && program.highlights.length"
      mt="16px"
    >
      <CBox class="hub-program-detail__panel">
        <div class="hub-program-detail__panel-header">
          <span class="hub-program-detail__icon">
            <OmtIcons
              :name="program.highlightsIcon || 'clipboard-list-check'"
              size="sm"
              color="var(--colors-light-primary-navy-blue)"
            />
          </span>
          <span class="hub-program-detail__panel-title">{{
            program.highlightsNote || "Diferenciais exclusivos*:"
          }}</span>
        </div>
        <ul class="hub-program-detail__list hub-program-detail__list--panel">
          <li
            v-for="item in program.highlights"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
      </CBox>
    </OmtRow>

    <OmtRow
      v-for="section in program.sections || []"
      :key="section.title"
      mt="16px"
    >
      <CBox class="hub-program-detail__panel">
        <div class="hub-program-detail__panel-header">
          <span class="hub-program-detail__icon">
            <OmtIcons
              :name="section.icon"
              size="sm"
              color="var(--colors-light-primary-navy-blue)"
            />
          </span>
          <span class="hub-program-detail__panel-title">{{ section.title }}</span>
        </div>
        <OmtTypography
          v-if="section.content"
          as="p1"
          color="var(--colors-light-primary-gray1)"
          class="hub-program-detail__panel-body"
          style="white-space: pre-line"
        >
          {{ section.content }}
        </OmtTypography>
        <ul
          v-if="section.items && section.items.length"
          class="hub-program-detail__list hub-program-detail__list--panel"
        >
          <li
            v-for="item in section.items"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
      </CBox>
    </OmtRow>

    <OmtRow
      v-if="program.howItWorks && program.howItWorks.length"
      mt="16px"
    >
      <button
        type="button"
        class="hub-program-detail__nav"
        @click="$emit('how-it-works')"
      >
        <span class="hub-program-detail__icon">
          <OmtIcons
            name="message-plus"
            size="sm"
            color="var(--colors-light-primary-navy-blue)"
          />
        </span>
        <span class="hub-program-detail__panel-title">Como o programa funciona</span>
      </button>
    </OmtRow>

    <OmtRow
      v-if="program.warning"
      mt="16px"
    >
      <CBox class="hub-program-detail__warning">
        <div class="hub-program-detail__panel-header">
          <span class="hub-program-detail__icon">
            <OmtIcons
              name="circle-info"
              size="sm"
              color="var(--colors-light-primary-navy-blue)"
            />
          </span>
          <span class="hub-program-detail__panel-title">Importante!</span>
        </div>
        <OmtTypography
          as="p1"
          color="var(--colors-light-primary-gray1)"
          class="hub-program-detail__panel-body"
        >
          {{ program.warning }}
        </OmtTypography>
      </CBox>
    </OmtRow>

    <OmtRow
      v-if="program.consent"
      mt="16px"
    >
      <OmtCheckbox
        :is-checked="accepted"
        @change="accepted = !accepted"
      >
        <OmtTypography
          as="p1"
          pl="8px"
          class="hub-program-detail__consent-text"
        >
          {{ program.consent.label }}
          <span
            class="hub-program-detail__consent-link"
            @click.prevent.stop="$emit('terms')"
          >
            {{ program.consent.linkLabel }}
          </span>
        </OmtTypography>
      </OmtCheckbox>
    </OmtRow>

    <OmtRow
      v-if="program.ctaLabel"
      mt="16px"
      mb="16px"
    >
      <CBox class="hub-program-detail__panel hub-program-detail__cta">
        <OmtTypography
          as="p1"
          color="var(--colors-light-primary-gray1)"
          class="hub-program-detail__cta-hint"
        >
          {{ ctaHint }}
        </OmtTypography>
        <OmtButton
          borderRadius="30px"
          width="100%"
          :is-disabled="isCtaDisabled"
          @click="onCtaClick"
        >
          <OmtTypography
            as="h1-bold"
            color="white"
          >
            {{ program.ctaLabel }}
          </OmtTypography>
        </OmtButton>
      </CBox>
    </OmtRow>
  </OmtLayoutContainer>
</template>

<script>
import HubBreadcrumb from "./HubBreadcrumb.vue";

export default {
  name: "HubProgramDetail",
  components: { HubBreadcrumb },
  props: {
    program: { type: Object, required: true },
    breadcrumb: { type: Array, default: () => [] }
  },
  data() {
    return {
      accepted: false
    };
  },
  computed: {
    ctaHint() {
      if (this.program.ctaHint) return this.program.ctaHint;
      if (this.program.ctaKind === "avaliacao") {
        return "Para participar, clique no botão abaixo:";
      }
      return "Para participar, entre em contato pelo botão abaixo:";
    },
    isCtaDisabled() {
      return Boolean(this.program.consent) && !this.accepted;
    }
  },
  watch: {
    "program.slug"() {
      this.accepted = false;
    }
  },
  methods: {
    onCtaClick() {
      if (this.isCtaDisabled) return;
      this.$emit("cta");
    }
  }
};
</script>

<style scoped>
.hub-program-detail {
  width: 100%;
}

.hub-program-detail__cover {
  width: calc(100% + 32px);
  max-width: unset;
  margin-left: -16px;
  margin-right: -16px;
  height: 180px;
  object-fit: cover;
  border-radius: 0;
}

.hub-program-detail__intro {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.hub-program-detail__intro >>> h1,
.hub-program-detail__intro >>> p {
  margin: 0 !important;
}

.hub-program-detail__list {
  margin: 12px 0 0 18px;
  color: var(--colors-light-primary-gray1);
  font-size: 14px;
  line-height: 22px;
}

.hub-program-detail__list--panel {
  margin-top: 12px;
}

.hub-program-detail__panel,
.hub-program-detail__warning {
  width: 100%;
  border: 1px solid var(--colors-light-secondary-divider);
  border-radius: 12px;
  padding: 16px;
  background: transparent;
}

.hub-program-detail__panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hub-program-detail__panel-title {
  font-family: "Open Sans", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: var(--colors-light-primary-navy-blue);
  margin: 0;
}

.hub-program-detail__panel-body {
  margin-top: 12px !important;
}

.hub-program-detail__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e4eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hub-program-detail__nav {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 0;
  border-radius: 12px;
  background: #eef0f6;
  cursor: pointer;
  text-align: left;
}

.hub-program-detail__consent-text {
  color: #4e4e4e;
  line-height: 22px;
}

.hub-program-detail__consent-link {
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.hub-program-detail__cta-hint {
  margin: 0 0 12px !important;
}

@media screen and (min-width: 1024px) {
  .hub-program-detail__cover {
    width: 100%;
    margin: 0;
    height: 280px;
    border-radius: 12px;
  }
}
</style>
